/**
 * Video Format Loader Module
 * Adds MP4 fallback sources to WebM videos for better browser compatibility
 * Uses HTML5 <source> tags to let browsers choose the best format
 */

import { fileExists } from '../utils/helpers.js';

/**
 * Get alternative video URL by replacing extension
 * @param {string} originalUrl - The original video URL
 * @param {string} newExtension - The new extension (e.g., 'mov', 'mp4')
 * @returns {string} - The new URL with replaced extension
 */
function getAlternativeUrl(originalUrl, newExtension) {
    return originalUrl.replace(/\.[^.]+$/, `.${newExtension}`);
}

/**
 * Process a single video element
 * Adds <source> tags for better browser compatibility
 * @param {HTMLVideoElement} videoElement - The video element to process
 */
async function processVideoElement(videoElement) {
    // Get the original video source URL
    let originalSrc = null;
    
    // Check if video has a direct src attribute
    if (videoElement.src) {
        originalSrc = videoElement.src;
    } else {
        // Check for existing source elements
        const existingSource = videoElement.querySelector('source');
        if (existingSource && existingSource.src) {
            originalSrc = existingSource.src;
        }
    }
    
    // If no WebM source found, nothing to do
    if (!originalSrc || !originalSrc.includes('.webm')) {
        return;
    }
    
    // Check if MP4 alternative exists
    const mp4Url = getAlternativeUrl(originalSrc, 'mp4');
    const mp4Exists = await fileExists(mp4Url);
    
    if (mp4Exists) {
        // Clear existing src attribute and any source elements
        videoElement.removeAttribute('src');
        videoElement.innerHTML = '';
        
        // Create source elements
        const webmSource = document.createElement('source');
        webmSource.src = originalSrc;
        webmSource.type = 'video/webm';
        
        const mp4Source = document.createElement('source');
        mp4Source.src = mp4Url;
        mp4Source.type = 'video/mp4';
        
        // Add sources: WebM first (better compression), MP4 as fallback
        videoElement.appendChild(webmSource);
        videoElement.appendChild(mp4Source);
        
        // Reload the video to apply the new sources
        videoElement.load();
        
        // console.log(`Video sources added: WebM (${originalSrc}) + MP4 fallback (${mp4Url})`);
    } else {
        // No MP4 alternative found
        if (videoElement.poster) {
            console.warn(`No MP4 alternative found for: ${originalSrc}. Poster image will be displayed.`);
        } else {
            console.warn(`No MP4 alternative or poster found for: ${originalSrc}`);
        }
    }
}

/**
 * Initialize video format loader
 * Processes all video elements on the page to add MP4 fallbacks
 */
export default function initVideoFormatLoader() {
    // Find all video elements
    const videos = document.querySelectorAll('video');
    
    if (videos.length === 0) {
        return;
    }
    
    // console.log(`Processing ${videos.length} video element(s) for MP4 fallbacks...`);
    
    // Process each video element
    videos.forEach(video => {
        processVideoElement(video).catch(error => {
            console.error('Error processing video element:', error);
        });
    });
}

